import { useState } from 'react';
import { Button, Dropdown, Form, Offcanvas } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import {
  useGetDomainsForUpdatingQuery,
  useUpdateRanksMutation
} from '@/app/KeywordTracker/api/keywordsApi';
import { Formik } from 'formik';
import { array, object, string } from 'yup';
import { errorMessage, successMessage } from '@/utils/toast';
import { MdPeopleAlt } from 'react-icons/md';
import SubtleBadge from '@/components/common/SubtleBadge';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import DomainFavicon from '@/app/common/components/DomainFavicon';
import classNames from 'classnames';
import _ from 'lodash';
import IconAlert from '@/components/common/IconAlert';
import useDomain from '@/hooks/useDomain';
import type { Dispatch, SetStateAction } from 'react';
import type { DrawerShowProps } from './types';

interface ManualUpdateSettingsDrawerProps extends DrawerShowProps {
  isOnUpdate: boolean;
  setIsOnUpdate: Dispatch<SetStateAction<boolean>>;
}

const AUTO_UPDATE_STATUS = 'فعال بودن به‌روزرسانی خودکار';

type DomainSort = 'oldest' | 'newest' | 'alphabetical';

const SORT_OPTIONS: { value: DomainSort; label: string }[] = [
  { value: 'oldest', label: 'قدیمی‌ترین' },
  { value: 'newest', label: 'جدیدترین' },
  { value: 'alphabetical', label: 'الفبایی' }
];

const ManualUpdateSettingsDrawer = ({
  show,
  setShow,
  setIsOnUpdate
}: ManualUpdateSettingsDrawerProps) => {
  const [showAlertIcon, setShowAlertIcon] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<DomainSort>('oldest');
  const { data: userInfo, isLoading, isError } = useGetUserInfoQuery();
  const { domain_slug, domain } = useDomain();
  const [updateRanks] = useUpdateRanksMutation();
  const { data: domainsForUpdate, isFetching } = useGetDomainsForUpdatingQuery(
    undefined,
    { skip: !show }
  );

  const searchQuery = search.trim().toLowerCase();
  const filteredDomains = (() => {
    const filtered = (domainsForUpdate?.data ?? []).filter(domainItem =>
      searchQuery
        ? [domainItem.show_name, domainItem.url]
            .filter(Boolean)
            .some(value => value.toLowerCase().includes(searchQuery))
        : true
    );

    if (sort === 'newest') {
      return [...filtered].reverse();
    }

    if (sort === 'alphabetical') {
      return [...filtered].sort((a, b) =>
        (a.url ?? '').localeCompare(b.url ?? '', 'fa', { sensitivity: 'base' })
      );
    }

    return filtered;
  })();
  const updatableVisibleCount = filteredDomains.filter(
    domainItem => domainItem.be_updated
  ).length;

  return (
    <Offcanvas
      onShow={() => {
        const offcanvasElement = document.querySelector('.offcanvas');
        if (offcanvasElement) {
          offcanvasElement.removeAttribute('tabindex');
        }
      }}
      show={show}
      onHide={() => {
        setSearch('');
        setSort('oldest');
        setShow(false);
      }}
      style={{ backgroundColor: 'var(--falcon-body-bg)' }}
    >
      <Offcanvas.Header closeButton className="flex-shrink-0">
        <Offcanvas.Title>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <div className="m-0">به‌روزرسانی دامنه‌ها </div>
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="domain-update-drawer-body d-flex flex-column p-0 pt-0">
        <div className="domain-update-drawer-scroll flex-grow-1 overflow-auto px-3 pt-0">
          {showAlertIcon && (
            <IconAlert
              onClose={() => setShowAlertIcon(false)}
              variant="info"
              dismissible={true}
            >
              به‌روزرسانی دستی به خاطر محدودیت‌های جدید گوگل ممکنه تا ۲۴ ساعت طول
              بکشه.{' '}
            </IconAlert>
          )}
          <Formik
            onSubmit={async (values, action) => {
              updateRanks(values)
                .unwrap()
                .then(res => {
                  if (res?.success) {
                    if (res?.data) {
                      const { message, updated_started } = res?.data;
                      if (updated_started) {
                        setIsOnUpdate(true);
                      }
                      successMessage(message);
                    }
                  } else {
                    errorMessage(
                      'عملیات به‌روزرسانی کلمات کلیدی با خطا مواجه شد.'
                    );
                  }
                })
                .catch(err => {
                  errorMessage('خطایی در به‌روزرسانی دامنه‌ها وجود داره');
                  console.error(err);
                });
              setShow(false);
              setSearch('');
              setSort('oldest');
            }}
            initialValues={{ domain_ids: [] }}
            enableReinitialize
            validationSchema={object().shape({
              domain_ids: array().of(string())
            })}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue
            }) =>
              !isLoading && (
                <Form
                  id="manual-update-domains-form"
                  onSubmit={e => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  {!isFetching && (
                    <>
                      <div className="d-flex align-items-center gap-2 mb-2 mt-2">
                        <Form.Control
                          size="sm"
                          type="search"
                          className="flex-grow-1"
                          value={search}
                          onChange={({ target }) => setSearch(target.value)}
                          placeholder="جستجو بر اساس آدرس یا نام نمایشی…"
                        />
                        <Dropdown align="start" className="flex-shrink-0">
                          <Dropdown.Toggle
                            size="sm"
                            variant="falcon-default"
                            className="d-flex align-items-center gap-1"
                          >
                            مرتب‌سازی
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="border py-0">
                            {SORT_OPTIONS.map(option => (
                              <Dropdown.Item
                                key={option.value}
                                active={sort === option.value}
                                className="fs--1 py-2"
                                onClick={() => setSort(option.value)}
                              >
                                {option.label}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>

                      <div className="d-flex align-items-center justify-content-between mb-1 px-1">
                        <Form.Check type="checkbox" id="all" className="mb-0">
                          <Form.Check.Input
                            type="checkbox"
                            name="all"
                            disabled={updatableVisibleCount === 0}
                            onChange={({ target }) => {
                              const visibleUpdatableIds = filteredDomains
                                .filter(domainItem => domainItem.be_updated)
                                .map(domainItem => domainItem.id);
                              if (target.checked) {
                                setFieldValue(
                                  'domain_ids',
                                  _.uniq([
                                    ...values.domain_ids,
                                    ...visibleUpdatableIds
                                  ])
                                );
                              } else {
                                setFieldValue(
                                  'domain_ids',
                                  values.domain_ids.filter(
                                    id => !visibleUpdatableIds.includes(id)
                                  )
                                );
                              }
                            }}
                            checked={
                              updatableVisibleCount > 0 &&
                              filteredDomains
                                .filter(domainItem => domainItem.be_updated)
                                .every(domainItem =>
                                  values.domain_ids.includes(domainItem.id)
                                )
                            }
                          />
                          <Form.Check.Label className="mb-0 fs--2 fw-semi-bold cursor-pointer ms-1">
                            انتخاب همه
                          </Form.Check.Label>
                        </Form.Check>
                        <span className="fs--2 text-500">
                          {filteredDomains.length} دامنه
                          {values.domain_ids.length > 0 &&
                            ` · ${values.domain_ids.length} انتخاب‌شده`}
                        </span>
                      </div>

                      {filteredDomains.length === 0 ? (
                        <div className="text-center text-500 fs--2 py-4 border border-dashed rounded-2">
                          دامنه‌ای پیدا نشد.
                        </div>
                      ) : (
                        <div className="domain-update-list d-flex flex-column gap-2">
                          {filteredDomains.map(domainItem => {
                            const isAutoUpdating =
                              domainItem.status === AUTO_UPDATE_STATUS;
                            const isChecked = values.domain_ids.includes(
                              domainItem.id
                            );
                            const showName = domainItem.show_name?.trim();
                            const showDisplayName =
                              Boolean(showName) &&
                              showName !== domainItem.url;

                            return (
                              <label
                                key={domainItem.id}
                                htmlFor={domainItem.id}
                                className={classNames(
                                  'domain-update-item d-flex align-items-center gap-2 mb-0 py-2 px-2 rounded-2 border cursor-pointer',
                                  {
                                    'domain-update-item--checked': isChecked,
                                    'domain-update-item--disabled':
                                      isAutoUpdating
                                  }
                                )}
                              >
                                <Form.Check.Input
                                  id={domainItem.id}
                                  className="flex-shrink-0 m-0"
                                  disabled={isAutoUpdating}
                                  type="checkbox"
                                  name={domainItem.id}
                                  onChange={({ target }) => {
                                    if (target.checked) {
                                      setFieldValue('domain_ids', [
                                        ...values.domain_ids,
                                        domainItem.id
                                      ]);
                                    } else {
                                      setFieldValue(
                                        'domain_ids',
                                        values.domain_ids.filter(
                                          id => id !== domainItem.id
                                        )
                                      );
                                    }
                                  }}
                                  checked={isChecked}
                                />
                                <DomainFavicon
                                  domain={domainItem.url}
                                  width={26}
                                  className="rounded flex-shrink-0"
                                />
                                <div className="min-w-0 flex-grow-1">
                                  <div className="d-flex align-items-center gap-1">
                                    <span className="fw-semi-bold text-truncate fs-0 lh-sm">
                                      {domainItem.url}
                                    </span>
                                    {domainItem.access_level != 1 && (
                                      <ChildrensTooltip html="دسترسی اشتراکی">
                                        <span className="d-inline-flex text-500 flex-shrink-0">
                                          <MdPeopleAlt size={14} />
                                        </span>
                                      </ChildrensTooltip>
                                    )}
                                  </div>
                                  <div className="d-flex align-items-center gap-1 flex-wrap fs--2 text-500 lh-sm mt-1">
                                    {showDisplayName && (
                                      <span className="text-truncate">
                                        {showName}
                                      </span>
                                    )}
                                    {showDisplayName && <span>·</span>}
                                    <span className="flex-shrink-0">
                                      {domainItem.num_of_keywords} کلمه
                                    </span>
                                    {domainItem.status && (
                                      <>
                                        <span>·</span>
                                        <SubtleBadge
                                          bg={
                                            isAutoUpdating ? 'warning' : 'info'
                                          }
                                          className="fs--2 py-0"
                                        >
                                          {domainItem.status}
                                        </SubtleBadge>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </Form>
              )
            }
          </Formik>
        </div>

        <div className="domain-update-drawer-footer flex-shrink-0 d-flex gap-1 justify-content-end px-3 py-3 border-top">
          <Button
            size="sm"
            className="d-flex gap-1 align-items-center"
            variant="outline-secondary"
            onClick={() => {
              setSearch('');
              setSort('oldest');
              setShow(false);
            }}
          >
            <FontAwesomeIcon icon="times" />
            لغو
          </Button>
          <Button
            size="sm"
            className="d-flex gap-1 align-items-center border-0"
            type="submit"
            form="manual-update-domains-form"
            variant="falcon-primary"
          >
            <FontAwesomeIcon icon="plus" />
            به روز کن
          </Button>
        </div>
      </Offcanvas.Body>

      <style>
        {`
 .offcanvas{
     width:420px !important;
      max-width:100% !important;
      display: flex !important;
      flex-direction: column;
      height: 100%;
    }
  .domain-update-drawer-body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .domain-update-drawer-scroll {
    padding-bottom: 1.5rem !important;
  }
  .domain-update-list {
    margin-bottom: 1rem;
  }
  .domain-update-drawer-footer {
    background: var(--falcon-body-bg);
    z-index: 2;
  }
  .offcanvas img:not(.domain-update-item img) {
    margin: 0 auto;
    max-width: 100%;
    height: auto;
    display: flex;
    }
  .domain-update-item img {
    margin: 0;
    display: inline-block;
    max-width: none;
    height: auto;
  }
  .domain-update-item {
    border-color: var(--falcon-border-color) !important;
    background: color-mix(in srgb, #000 14%, var(--falcon-body-bg));
    transition: border-color .15s ease, background-color .15s ease;
  }
  .domain-update-item:hover {
    background: color-mix(in srgb, #000 20%, var(--falcon-body-bg));
  }
  .domain-update-item--checked {
    border-color: var(--falcon-primary) !important;
    background: color-mix(in srgb, var(--falcon-primary) 14%, color-mix(in srgb, #000 14%, var(--falcon-body-bg)));
  }
  .domain-update-item--disabled {
    opacity: .65;
    cursor: not-allowed !important;
  }
  .domain-update-item--disabled:hover {
    background: color-mix(in srgb, #000 14%, var(--falcon-body-bg));
  }
  [data-bs-theme='light'] .domain-update-item {
    background: #fff;
  }
  [data-bs-theme='light'] .domain-update-item:hover {
    background: color-mix(in srgb, #000 3%, #fff);
  }
  [data-bs-theme='light'] .domain-update-item--checked {
    background: color-mix(in srgb, var(--falcon-primary) 10%, #fff);
  }
  [data-bs-theme='light'] .domain-update-item--disabled:hover {
    background: #fff;
  }
 `}
      </style>
    </Offcanvas>
  );
};

export default ManualUpdateSettingsDrawer;
